import { Icon } from '@iconify/react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { useState } from 'react'
import { Seo } from '@/seo/Seo'
import { useStorefrontContent, useBranding } from '@/branding/BrandingProvider'

export function SignInPage() {
  const { login } = useAuth()
  const { auth } = useStorefrontContent()
  const { siteName } = useBranding()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await login(email, password, rememberMe)
      const returnTo = sessionStorage.getItem('storefront-return-to') || '/'
      sessionStorage.removeItem('storefront-return-to')
      navigate(returnTo, { replace: true })
    } catch (err) {
      // Pull the HTTP status + the backend's own message (ApiError carries both),
      // so we can guide the user instead of showing "Invalid email or password"
      // for every failure.
      const status = (err as any)?.status as number | undefined
      const body = (err as any)?.body as string | undefined
      let detail = ''
      try { detail = body ? (JSON.parse(body)?.error || '') : '' } catch { detail = body || '' }
      const msg = (detail || (err instanceof Error ? err.message : '')).toLowerCase()

      if (msg.includes('not fully set up') || msg.includes('setup incomplete')) {
        setError('Your account isn’t finished setting up yet. Use “Forgot password?” below to set a password and complete it.')
      } else if (msg.includes('does not exist') || msg.includes('no account') || status === 404) {
        setError('No account found for this email. Use “Sign up” below to create one.')
      } else if (status === 401 || msg.includes('invalid') || msg.includes('credentials')) {
        setError('Invalid email or password. If you just signed up, check your email for a verification link, or reset your password below.')
      } else if (status === 502 || msg.includes('unavailable') || msg.includes('502')) {
        setError('Authentication service temporarily unavailable. Please try again in a moment.')
      } else {
        setError(detail || (err instanceof Error ? err.message : 'Sign-in failed. Please try again.'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen flex bg-white font-sans">
      <Seo
        title={`Sign In — ${siteName}`}
        description={`Sign in to ${siteName}. ${auth.signIn.subtitle}`}
        path="/sign-in"
      />
      {/* Left Side: Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12">
        <div className="w-full max-w-md mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-12 transition-colors"
          >
            <Icon icon="solar:arrow-left-linear" /> Back to Main Site
          </Link>

          <div className="mb-10">
            <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">{auth.signIn.title}</h1>
            <p className="text-slate-500 font-light">{auth.signIn.subtitle}</p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="accent-navy"
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-sm text-rust hover:text-navy transition-colors">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <span className="text-sm text-slate-500 font-light">Don&apos;t have an account? </span>
            <Link to="/account-type" className="text-sm font-medium text-rust hover:text-navy transition-colors">
              Create Account
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side: Visual */}
      <div className="hidden lg:flex w-1/2 bg-navy relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
                <path d="M25 0 L50 14.4 L50 43.4 L25 57.8 L0 43.4 L0 14.4 Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexagons)" className="text-white" />
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-[2rem] shadow-2xl">
          <div className="w-12 h-12 rounded-full bg-rust/20 flex items-center justify-center mb-6">
            <Icon icon="solar:shield-keyhole-linear" className="text-2xl text-rust" />
          </div>
          <h3 className="text-2xl font-medium tracking-tight text-white mb-4">Privacy by Design</h3>
          <p className="text-white/80 font-light leading-relaxed mb-6">
            &ldquo;Zenzers ensures our patients&apos; dignity. Data is processed locally on the Edge and transmitted via
            encrypted channels. It feels like a premium SaaS product, not an intrusive panic button.&rdquo;
          </p>
          <div className="flex items-center gap-4 border-t border-white/10 pt-6">
            <div className="w-10 h-10 rounded-full bg-slate-800 border border-white/20" />
            <div>
              <div className="text-sm font-medium text-white">Dr. Sarah Jenkins</div>
              <div className="text-xs text-white/50 font-light">Chief of Geriatrics, Memorial Health</div>
            </div>
          </div>
        </div>

        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-rust/20 rounded-full blur-[100px] pointer-events-none" />
      </div>
    </main>
  )
}
